/**
 * Phase 2.5: Audio Waveform Visualizer
 * 
 * Displays a live waveform during dictation based on audio level.
 * Purely visual — no data capture or processing.
 */

import { useMemo } from 'react';
import { cn } from '@/lib/utils';

interface AudioWaveformProps {
  audioLevel: number; // 0-1
  isRecording: boolean;
  barCount?: number;
  className?: string;
}

export function AudioWaveform({ audioLevel, isRecording, barCount = 32, className }: AudioWaveformProps) {
  const bars = useMemo(() => {
    return Array.from({ length: barCount }, (_, i) => {
      // Create a natural waveform shape with center emphasis
      const centerDistance = Math.abs(i - barCount / 2) / (barCount / 2);
      const baseHeight = isRecording
        ? Math.max(0.08, (1 - centerDistance * 0.7) * audioLevel)
        : 0.08;
      // Add slight randomness for organic feel
      const jitter = isRecording ? (Math.random() * 0.15 * audioLevel) : 0;
      return Math.min(1, baseHeight + jitter);
    });
  }, [audioLevel, isRecording, barCount]);

  return (
    <div className={cn('flex items-center justify-center gap-[2px] h-16', className)}>
      {bars.map((height, i) => (
        <div
          key={i}
          className={cn(
            'w-1 rounded-full transition-all duration-75',
            isRecording ? 'bg-primary' : 'bg-muted-foreground/30'
          )}
          style={{ height: `${Math.max(4, height * 64)}px` }}
        />
      ))}
    </div>
  );
}
